import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import { ClipLoader } from "react-spinners";
import { useTheme } from "../../../context/ThemeProvider";
import { useCallHistory } from "../../../store/api/useCallHistory";
import CallInCallHistory from "./CallInCallHistory";
import { formatDate, formatTime } from "../../../utils/formatUTCtoLocalDate";
import ShowSavedName from "../../../components/ShowSavedName";
import SearchNotFoundAnimation from "../../../components/SearchNotFoundAnimation";
import { useTimeFormatOptions } from "../../../hooks/useTimeFormatOptions";
import TextTranslate from "../../../utils/TextTranslate";
import { useAppSelector } from "../../../utils/hooks";
import {
  resolveCallHistoryDirection,
  resolveCallHistoryPeer,
} from "../../../utils/callHistoryPeer";

export default function MissedCallHistory() {
  const { data: callHistory, isLoading } = useCallHistory();
  const userData = useAppSelector((state) => state.userData);
  // @ts-ignore
  const { theme } = useTheme();
  const { hour12 } = useTimeFormatOptions();

  // Only true missed (someone called us). Own unanswered = outgoing, not here.
  const missedCalls = (callHistory?.data?.Records || []).filter(
    (record) =>
      record?.Calls?.length > 0 &&
      resolveCallHistoryDirection(record, userData.user_id) === "missed",
  );

  if (isLoading) {
    return (
      <div className="grid min-h-[50vh] place-content-center">
        <ClipLoader size={22} color={theme === "dark" ? "white" : "black"} />
      </div>
    );
  }

  if (!missedCalls.length) {
    return (
      <div className="grid min-h-[50vh] place-content-center gap-4 text-center">
        <SearchNotFoundAnimation />
        <div className="text-[15px] text-lightText">
          <TextTranslate text="No Missed Calls Found" />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
      {missedCalls.map((record, index) => {
        const call = record.Calls[0];
        const isGroup = record.Chat.chat_type === "group";
        const peer = resolveCallHistoryPeer(record, userData.user_id);
        const profileImage = isGroup
          ? record.Chat.group_icon
          : peer?.profile_pic || call?.caller?.profile_pic;

        return (
          <div
            key={(record as any).id || `${record.createdAt}-${index}`}
            className={`flex items-center gap-3 px-3 py-3 ${
              index > 0
                ? "border-t border-black/[0.06] dark:border-white/[0.08]"
                : ""
            }`}
          >
            <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full">
              <LoadingSkeletonImageDynamic
                radius=""
                className="h-full w-full rounded-full object-cover"
                image_height=""
                image_width=""
                image_url={profileImage}
              />
            </div>

            <div className="min-w-0 flex-1">
              <div className="truncate text-[15px] font-semibold capitalize text-[#FF3B30]">
                {isGroup ? (
                  record.Chat.group_name
                ) : (
                  <ShowSavedName
                    user_id={(
                      peer?.user_id || call?.caller?.user_id
                    )?.toString()}
                    name={
                      peer?.full_name ||
                      peer?.user_name ||
                      call?.caller?.user_name
                    }
                  />
                )}
              </div>
              <CallInCallHistory callData={record} />
            </div>

            <div className="shrink-0 text-right text-[11px] tabular-nums text-lightText">
              <div>{formatDate(record.createdAt.toString())}</div>
              <div className="mt-0.5">
                {formatTime(record.createdAt.toString(), { hour12 })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
